import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { graphql } from 'gatsby';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import useModal from '@/components/Modal/hooks/useModal';
import {
  Backdrop,
  Container,
  Wrapper,
  Dialog,
  CloseButton,
  Content,
  Header,
  Title,
  StyledEyebrowWrapper,
  StyledEyebrow,
} from './ModalV2.styles';
import CloseIcon from '@/components/Icon/components/CloseIcon/CloseIcon';

const NewsletterForm = lazy(() => import('../Forms/NewsletterForm/NewsletterForm'));
const ModalContentPricing = lazy(() => import('./content/ModalContentPricing/ModalContentPricing'));
const ModalContentDownloadEdition = lazy(
  () => import('./content/ModalContentDownloadEdition/ModalContentDownloadEdition')
);
const ContactForm = lazy(() => import('@/components/Forms/ContactForm/ContactForm'));
const EditionsContactForm = lazy(
  () => import('@/components/Forms/EditionsContactForm/EditionsContactForm')
);
const ProductContactForm = lazy(
  () => import('@/components/Forms/ProductContactForm/ProductContactForm')
);
const CampaignForm = lazy(() => import('@/components/CampaignForm/CampaignForm'));
const FormContainer = lazy(() => import('../FormContainer/FormContainer'));
const Iframe = lazy(() => import('../Iframe/Iframe'));

const ModalV2 = ({ modalV2Elements: elements }: Queries.MODAL_V2Fragment) => {
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  const modalID = elements.url_slug.value;
  const modalTitle = elements.title.value;
  const modalEyebrow = elements.eyebrow?.value ?? '';
  const modalSize = elements.size.value;
  const shouldRenderTitle = elements.options?.value?.includes('show_title') ?? false;
  const portalId = `modal-v2-container-${modalID}`;
  const { close, isOpen } = useModal();
  const ref = useRef(null);

  const handleOnClose = () => {
    close(modalID);
  };

  useLayoutEffect(() => {
    if (document) {
      document.body.style.overflowY = isOpen() ? 'hidden' : 'scroll';
    }
  }, [isOpen, modalID]);

  useOnClickOutside(ref, () => {
    handleOnClose();
  });

  useEffect(() => {
    if (document && !document.getElementById(portalId)) {
      const divElement = document.createElement('div');
      divElement.setAttribute('id', portalId);
      document.body.appendChild(divElement);

      setPortalContainer(divElement);

      return () => {
        document.body.removeChild(divElement);
      };
    }
  }, [portalId]);

  const content = elements.content.value;
  const component = useMemo(
    () => (
      <Suspense>
        {content.__typename === 'kontent_item_modal_content_pricing' && (
          <ModalContentPricing {...content} />
        )}
        {content.__typename === 'kontent_item_modal_content_download_edition' && (
          <ModalContentDownloadEdition {...content} />
        )}
        {content.__typename === 'kontent_item_contact_form' && <ContactForm {...content} />}
        {content.__typename === 'kontent_item_editions_contact_form' && (
          <EditionsContactForm {...content} />
        )}
        {content.__typename === 'kontent_item_product_contact_form' && (
          <ProductContactForm {...content} />
        )}
        {content.__typename === 'kontent_item_newsletter_form' && <NewsletterForm {...content} />}
        {content.__typename === 'kontent_item_campaign_form' && <CampaignForm {...content} />}
        {content.__typename === 'kontent_item_form_container' && <FormContainer {...content} />}
        {content.__typename === 'kontent_item_iframe' && <Iframe {...content} />}
      </Suspense>
    ),
    [content]
  );

  if (!isOpen(modalID) || !portalContainer) return null;

  return createPortal(
    <Backdrop id={modalID}>
      <Container>
        <Wrapper>
          <Dialog role="dialog" aria-labelledby={modalTitle} ref={ref}>
            <Header>
              <CloseButton onClick={handleOnClose} aria-label="Close modal">
                <CloseIcon variant="close_icon_basic_dark" />
              </CloseButton>
            </Header>
            <Content size={modalSize}>
              {modalEyebrow && (
                <StyledEyebrowWrapper>
                  <StyledEyebrow>{modalEyebrow}</StyledEyebrow>
                </StyledEyebrowWrapper>
              )}
              {shouldRenderTitle && <Title>{modalTitle}</Title>}
              {component}
            </Content>
          </Dialog>
        </Wrapper>
      </Container>
    </Backdrop>,
    portalContainer
  );
};

export default ModalV2;

export const fragment = graphql`
  fragment MODAL_V2 on kontent_item_modal_v2 {
    system {
      id
    }
    preferred_language
    modalV2Elements: elements {
      title {
        value
      }
      eyebrow {
        value
      }
      url_slug {
        value
      }
      size {
        value
      }
      options {
        value
      }
      content {
        value {
          ...MODAL_CONTENT_PRICING
          ...MODAL_CONTENT_DOWNLOAD_EDITION
          ...CONTACT_FORM
          ...PRODUCT_CONTACT_FORM
          ...EDITIONS_CONTACT_FORM
          ...NEWSLETTER_FORM
          ...CAMPAIGN_FORM
          ...FORM_CONTAINER
          ...IFRAME
        }
      }
    }
  }
`;
